import React from 'react';
import { Stack, Button } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import * as ExcelJS from 'exceljs';

interface StatementData {
  date: string;
  voucher: string;
  narration: string;
  debit: string;
  credit: string;
  balance: string;
}

interface DownloadButtonsProps {
  data: StatementData[];
  pdfDocument: React.ReactElement;
  fileName?: string;
}

const DownloadButtons: React.FC<DownloadButtonsProps> = ({ 
  data, 
  pdfDocument, 
  fileName = 'statement_of_account_daniel_bvurire.pdf' 
}) => {
  const downloadCSV = () => {
    const header = ['Txn Date', 'Reference', 'Narration', 'Debit', 'Credit', 'Balance'];
    const rows = data.map(txn => [txn.date, txn.voucher, txn.narration, txn.debit, txn.credit, txn.balance]);
    const csvContent = [header, ...rows].map(e => e.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'statement.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Statement');
      
      // Add headers
      worksheet.addRow(['Txn Date', 'Reference', 'Narration', 'Debit', 'Credit', 'Balance']);
      
      // Add data rows
      data.forEach(txn => {
        worksheet.addRow([txn.date, txn.voucher, txn.narration, txn.debit, txn.credit, txn.balance]);
      });
      
      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'statement.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel file. Please try again.');
    }
  };

  const printTable = () => {
    const printContent = document.getElementById('statement-table');
    const printWindow = window.open('', '', 'width=900,height=700');
    if (printWindow && printContent) {
      printWindow.document.write('<html><head><title>Print Statement</title>');
      printWindow.document.write('</head><body >');
      printWindow.document.write(printContent.outerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
      <PDFDownloadLink
        document={pdfDocument}
        fileName={fileName}
        style={{
          textDecoration: 'none',
          padding: '10px 20px',
          backgroundColor: '#1976d2',
          color: '#fff',
          borderRadius: 4,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        {({ loading }) => loading ? <span>Generating PDF...</span> : <span>Download PDF</span>}
      </PDFDownloadLink>
      <Button variant="contained" color="success" onClick={downloadCSV}>
        Download CSV
      </Button>
      <Button variant="contained" color="warning" onClick={downloadExcel}>
        Download Excel
      </Button>
      <Button variant="contained" color="secondary" onClick={printTable}>
        Print
      </Button>
    </Stack>
  );
};

export default DownloadButtons; 